import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { messages } from '../i18n/ja';
import {
  isClean,
  isOverlayShown,
  isShown,
  loadLayout,
  OVERLAY_IDS,
  offsetOf,
  placementOf,
  saveLayout,
  withAllHidden,
  withAllShown,
  withCollapsed,
  withOverlay,
  withPlacement,
  withReset,
  withShown,
  type FloatingId,
  type LayoutKind,
  type Offset,
  type OverlayId,
  type Size,
  type WindowId,
  type WindowLayout,
  type WindowPlacement,
} from './window-layout';

/** 配置の操作（{@link WindowLayoutProvider} が配る）。 */
export interface WindowLayoutApi {
  /** いまの画面の状態（通常・全画面）。配置は別々に覚える */
  readonly kind: LayoutKind;
  readonly layout: WindowLayout;
  /** ウィンドウの位置を変える。`persist` が真なら、端末に保存する */
  readonly setPlacement: (id: WindowId, placement: WindowPlacement, persist: boolean) => void;
  /** いまの画面の状態の配置を、元に戻す */
  readonly reset: () => void;
  /** 全画面のときに、ツールバー・パネルを出すか */
  readonly setShown: (id: FloatingId, shown: boolean) => void;
  readonly isShown: (id: FloatingId) => boolean;
  /** 全画面のときに、3D の上の表示（HUD・視点・再生の操作など）を出すか */
  readonly setOverlay: (id: OverlayId, shown: boolean) => void;
  readonly isOverlayShown: (id: OverlayId) => boolean;
  /** 全画面の操作を、小さなボタン 1 つにたたんでいるか。たたむ・開く */
  readonly collapsed: boolean;
  readonly setCollapsed: (collapsed: boolean) => void;
  /** 画面をすっきりさせる（すべて隠す）。すでにすっきりなら、表示を戻す */
  readonly toggleClean: () => void;
  /** 全画面で、すっきりさせているか */
  readonly clean: boolean;
  /** いまの画面の状態で、動かしたウィンドウがあるか */
  readonly moved: boolean;
}

const WindowLayoutContext = createContext<WindowLayoutApi | undefined>(undefined);

/** ウィンドウの配置を、子のコンポーネントへ配る。配置は端末ごとに保存する。 */
export function WindowLayoutProvider({
  kind,
  children,
}: {
  readonly kind: LayoutKind;
  readonly children: ReactNode;
}) {
  const [layout, setLayout] = useState<WindowLayout>(() => loadLayout());
  const update = useCallback((next: (current: WindowLayout) => WindowLayout, persist: boolean) => {
    setLayout((current) => {
      const value = next(current);
      if (persist) saveLayout(value);
      return value;
    });
  }, []);
  const api = useMemo<WindowLayoutApi>(
    () => ({
      kind,
      layout,
      setPlacement: (id, placement, persist) => {
        update((current) => withPlacement(current, kind, id, placement), persist);
      },
      reset: () => {
        update((current) => withReset(current, kind), true);
      },
      setShown: (id, shown) => {
        update((current) => withShown(current, id, shown), true);
      },
      isShown: (id) => isShown(layout, id),
      setOverlay: (id, shown) => {
        update((current) => withOverlay(current, id, shown), true);
      },
      isOverlayShown: (id) => isOverlayShown(layout, id),
      collapsed: layout.collapsed,
      setCollapsed: (collapsed) => {
        update((current) => withCollapsed(current, collapsed), true);
      },
      toggleClean: () => {
        update(
          (current) => (isClean(current) ? withAllShown(current) : withAllHidden(current)),
          true,
        );
      },
      clean: isClean(layout),
      moved: Object.keys(layout[kind]).length > 0,
    }),
    [kind, layout, update],
  );
  return <WindowLayoutContext.Provider value={api}>{children}</WindowLayoutContext.Provider>;
}

/** 配置の操作（{@link WindowLayoutProvider} の外では `undefined`）。 */
export function useWindowLayout(): WindowLayoutApi | undefined {
  return useContext(WindowLayoutContext);
}

/** 矢印キーで動かす量 [px]（Shift と一緒なら 4 倍）。 */
const KEY_STEP = 16;

/** {@link useMovableWindow} の戻り値。 */
export interface MovableWindow<T extends HTMLElement> {
  /** 動かす要素に付ける（コールバック ref。要素は、親要素の中で `position: absolute` にしておく） */
  readonly attach: (element: T | null) => void;
  /** 動かす要素（付く前は `null`） */
  readonly element: T | null;
  /** 動かした位置（動かしていなければ `undefined` = CSS の既定の位置） */
  readonly style: CSSProperties | undefined;
  /** 動かすための持ち手（ボタン）。ウィンドウの中に置く */
  readonly handle: ReactNode;
}

/** 入れ物（親要素）の、ウィンドウを置ける範囲（内側の余白 = 画面の安全領域などを除く）。 */
interface Area {
  readonly size: Size;
  /** 置ける範囲の左上（親要素の内側の余白の分） */
  readonly left: number;
  readonly top: number;
}

/** 親要素の中で、ウィンドウを置ける範囲。 */
function areaOf(parent: HTMLElement): Area {
  const computed = getComputedStyle(parent);
  const left = Number.parseFloat(computed.paddingLeft) || 0;
  const right = Number.parseFloat(computed.paddingRight) || 0;
  const top = Number.parseFloat(computed.paddingTop) || 0;
  const bottom = Number.parseFloat(computed.paddingBottom) || 0;
  return {
    size: {
      width: Math.max(0, parent.clientWidth - left - right),
      height: Math.max(0, parent.clientHeight - top - bottom),
    },
    left,
    top,
  };
}

/** 置ける範囲の中での、いまの左上の位置（transform による移動も含めた、見えている位置）。 */
function currentOffset(element: HTMLElement, parent: HTMLElement, area: Area): Offset {
  const rect = element.getBoundingClientRect();
  const base = parent.getBoundingClientRect();
  return {
    left: rect.left - base.left - parent.clientLeft - area.left,
    top: rect.top - base.top - parent.clientTop - area.top,
  };
}

/**
 * ウィンドウを、持ち手のドラッグ（指・マウス）と矢印キーで動かせるようにする。
 * 位置は、親要素の中の余白に対する割合で覚えるので、画面の大きさ・向きが変わってもはみ出さない。
 * 動かしたウィンドウの幅は CSS のまま（右端を指定しないので、CSS で幅を決めておく）。
 *
 * @param id ウィンドウの種類（配置の保存に使う）
 * @param label ウィンドウの名前（持ち手の読み上げ・ツールチップ）
 */
export function useMovableWindow<T extends HTMLElement>(
  id: WindowId,
  label: string,
): MovableWindow<T> {
  const api = useWindowLayout();
  const [element, setElement] = useState<T | null>(null);
  const placement = api?.layout[api.kind][id];
  const [geometry, setGeometry] = useState<{ size: Size; area: Area } | undefined>();
  // ドラッグの開始点（指・マウスの位置と、ウィンドウの位置）
  const [drag, setDrag] = useState<
    | {
        readonly pointerId: number;
        /** ドラッグを始めたときの要素（表示が消えて付け直されたら、そのドラッグは無効） */
        readonly element: HTMLElement;
        readonly x: number;
        readonly y: number;
        readonly left: number;
        readonly top: number;
      }
    | undefined
  >();

  // ウィンドウ・親要素の大きさが変わったら（画面の回転・表示の切り替えを含む）、置き直す。
  // 親要素は、ウィンドウが隠れている（display: none）ときも見張る
  useLayoutEffect(() => {
    const parent = element?.parentElement;
    if (!element || !parent) return undefined;
    const update = (): void => {
      const size = { width: element.offsetWidth, height: element.offsetHeight };
      const area = areaOf(parent);
      setGeometry((current) =>
        current &&
        current.size.width === size.width &&
        current.size.height === size.height &&
        current.area.size.width === area.size.width &&
        current.area.size.height === area.size.height &&
        current.area.left === area.left &&
        current.area.top === area.top
          ? current
          : { size, area },
      );
    };
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    observer.observe(parent);
    return () => {
      observer.disconnect();
    };
  }, [element]);

  const moveTo = useCallback(
    (left: number, top: number, persist: boolean) => {
      const parent = element?.parentElement;
      if (!api || !element || !parent) return;
      const size = { width: element.offsetWidth, height: element.offsetHeight };
      const area = areaOf(parent);
      setGeometry({ size, area });
      api.setPlacement(id, placementOf({ left, top }, size, area.size), persist);
    },
    [api, id, element],
  );

  // 全画面で隠している 3D の上の表示は、描かない（位置・大きさの記録は残す）
  const overlayHidden =
    api !== undefined &&
    api.kind === 'fullscreen' &&
    (OVERLAY_IDS as readonly string[]).includes(id) &&
    !api.isOverlayShown(id as OverlayId);
  const style = useMemo<CSSProperties | undefined>(() => {
    // 隠れている（大きさ 0）ときは、CSS の既定の位置のまま
    const moved =
      placement === undefined || geometry === undefined || geometry.size.width === 0
        ? undefined
        : (() => {
            const offset = offsetOf(placement, geometry.size, geometry.area.size);
            return {
              left: geometry.area.left + offset.left,
              top: geometry.area.top + offset.top,
              right: 'auto',
              bottom: 'auto',
              transform: 'none',
              margin: 0,
            } satisfies CSSProperties;
          })();
    return overlayHidden ? { ...moved, display: 'none' } : moved;
  }, [placement, geometry, overlayHidden]);

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>): void => {
    const parent = element?.parentElement;
    if (!element || !parent) return;
    // 3D ビューの視点の操作・長押しのメニューにしない
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const offset = currentOffset(element, parent, areaOf(parent));
    setDrag({ pointerId: event.pointerId, element, x: event.clientX, y: event.clientY, ...offset });
  };
  /** いまの要素で、この指・マウスで始めたドラッグ */
  const current = (event: PointerEvent<HTMLButtonElement>): typeof drag =>
    drag?.pointerId === event.pointerId && drag.element === element ? drag : undefined;
  const onPointerMove = (event: PointerEvent<HTMLButtonElement>): void => {
    const start = current(event);
    if (!start) return;
    event.stopPropagation();
    // マウスのボタンを離している（離したことを取りこぼした）ときは、動かさない
    if (event.pointerType === 'mouse' && event.buttons === 0) {
      setDrag(undefined);
      return;
    }
    moveTo(start.left + event.clientX - start.x, start.top + event.clientY - start.y, false);
  };
  const onPointerUp = (event: PointerEvent<HTMLButtonElement>): void => {
    const start = current(event);
    if (!start) return;
    setDrag(undefined);
    moveTo(start.left + event.clientX - start.x, start.top + event.clientY - start.y, true);
  };
  // 取り消し（ほかの操作に奪われたなど）のときは、それまでに動かした位置を保存する
  const onPointerCancel = (event: PointerEvent<HTMLButtonElement>): void => {
    if (!current(event)) return;
    setDrag(undefined);
    if (api && placement !== undefined) api.setPlacement(id, placement, true);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    const parent = element?.parentElement;
    if (!element || !parent) return;
    const step = event.shiftKey ? KEY_STEP * 4 : KEY_STEP;
    const delta: Record<string, readonly [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const move = delta[event.key];
    if (!move) return;
    // 走行中の操作（↑ で加速）などに渡さない
    event.preventDefault();
    event.stopPropagation();
    const offset = currentOffset(element, parent, areaOf(parent));
    moveTo(offset.left + move[0], offset.top + move[1], true);
  };

  const t = messages.windows;
  const handle =
    api === undefined ? null : (
      <button
        type="button"
        className="move-handle"
        aria-label={t.move(label)}
        title={t.move(label)}
        data-testid={`move-${id}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onLostPointerCapture={(event) => {
          if (drag?.pointerId === event.pointerId) setDrag(undefined);
        }}
        onKeyDown={onKeyDown}
        onContextMenu={(event) => {
          event.preventDefault();
        }}
      >
        <span aria-hidden="true">⠿</span>
      </button>
    );

  return { attach: setElement, element, style, handle };
}
